import React, { useEffect, useRef, useMemo } from "react";
import { Card } from "react-bootstrap";
import SystemNotification from "./SystemNotification";

const COLORS = [
  "#007bff", // blue
  "#28a745", // green
  "#fd7e14", // orange
  "#6f42c1", // purple
  "#20c997", // teal
  "#e83e8c", // pink
  "#17a2b8", // cyan
];

const MessageList = ({ messages, currentUser }) => {
  const bottomRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Assign consistent color for each unique sender
  const senderColors = useMemo(() => {
    const map = new Map();
    let colorIndex = 0;
    messages.forEach((msg) => {
      if (!map.has(msg.senderId)) {
        map.set(msg.senderId, COLORS[colorIndex % COLORS.length]);
        colorIndex++;
      }
    });
    return map;
  }, [messages]);

  return (
    <div className="flex-grow-1 overflow-auto p-3" style={{ background: "#f8f9fa" }}>
      {messages.length === 0 && (
        <div className="text-muted text-center mt-5">No messages yet</div>
      )}

      {messages.map((msg) => {
        // Handle system notifications
        if (msg.messageType === 'system') {
          return (
            <SystemNotification
              key={msg._id || Math.random()}
              message={msg.message}
              metadata={msg.metadata}
              timestamp={msg.createdAt}
              fileAttachment={msg.fileAttachment}
            />
          );
        }

        // Handle regular messages
        const isOwn = msg.senderId === currentUser?._id;
        const senderName =
          msg.senderName || (isOwn ? "You" : "Unknown");
        const senderColor = senderColors.get(msg.senderId) || "#6c757d";

        return (
          <div
            key={msg._id || Math.random()}
            className={`d-flex mb-3 ${isOwn ? "justify-content-end" : "justify-content-start"}`}
          >
            <Card
              className={`p-2 shadow-sm ${isOwn ? "bg-primary text-white" : "bg-light"}`}
              style={{
                maxWidth: "70%",
                wordBreak: "break-word",
                borderRadius: "1rem",
              }}
            >
              {/* Sender name inside card */}
              {!isOwn && (
                <div
                  className="fw-semibold mb-1"
                  style={{
                    color: senderColor,
                    fontSize: "0.85rem",
                  }}
                >
                  {senderName}
                </div>
              )}

              {/* File preview or download */}
              {msg.fileUrl && (
                <div className="mb-2">
                  {msg.fileType?.startsWith("image/") ? (
                    <img
                      src={msg.fileUrl}
                      alt="attachment"
                      className="img-fluid rounded"
                      style={{ maxHeight: "200px" }}
                    />
                  ) : (
                    <a
                      href={msg.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none fw-semibold"
                      style={{ color: isOwn ? "#fff" : "#007bff" }}
                    >
                      <img src="/images/files.svg" alt="files" />{" "}
                      {msg.fileName || "Download File"}
                    </a>
                  )}
                </div>
              )}

              {/* File description */}
              {msg.description && (
                <div className="mt-2 mb-1" style={{ 
                  fontStyle: "italic",
                  opacity: 0.9,
                  fontSize: "0.95rem"
                }}>
                  {msg.description}
                </div>
              )}

              {/* Text message */}
              {msg.message && <div>{msg.message}</div>}

              {/* Timestamp */}
              <div
                className={`mt-1 small ${isOwn ? "text-light opacity-75" : "text-muted"}`}
                style={{ fontSize: "0.75rem" }}
              >
                {new Date(msg.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </Card>
          </div>
        );
      })}

      <div ref={bottomRef} />
    </div>
  );
};

export default MessageList;
